import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Users } from 'lucide-react';
import { referentApi } from '../../../lib/api';

const EMPTY = { first_name: '', last_name: '', title: '', email: '' };

export function ReferentsSettings() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const emailRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await referentApi.list();
            setItems(data);
        } catch (e) {
            toast.error('Referentenliste konnte nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing({ __new: true }); setForm(EMPTY); };
    const openEdit = (r) => {
        setEditing(r);
        setForm({ first_name: r.first_name || '', last_name: r.last_name || '', title: r.title || '', email: r.email || '' });
    };
    const closeDialog = () => { setEditing(null); setForm(EMPTY); };

    const save = async () => {
        if (!form.first_name.trim() || !form.last_name.trim()) {
            toast.error('Vor- und Nachname sind Pflicht.');
            return;
        }
        setSaving(true);
        try {
            // E-Mail-Feld ist uncontrolled (via Ref), um Firefox-spezifische
            // Eingabe-Blockaden zu umgehen — Wert erst beim Speichern auslesen.
            const emailValue = (emailRef.current?.value || '').trim();
            const payload = { ...form, email: emailValue };
            if (editing?.__new) {
                await referentApi.create(payload);
                toast.success('Referent angelegt.');
            } else {
                await referentApi.update(editing.id, payload);
                toast.success('Änderungen gespeichert.');
            }
            closeDialog();
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Speichern.');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (r, active) => {
        try {
            await referentApi.update(r.id, { active });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Status konnte nicht geändert werden.');
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`"${r.display_name}" wirklich löschen?`)) return;
        try {
            await referentApi.delete(r.id);
            toast.success('Referent gelöscht.');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen.');
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <CardTitle className="flex items-center gap-2 font-heading">
                            <Users className="h-5 w-5" /> Referenten
                        </CardTitle>
                        <CardDescription className="mt-1">
                            Stammdaten der BGPR-Referenten. Werden in der Seminar-Bearbeitung per
                            Dropdown ausgewählt. Bucht sich ein Referent mit hinterlegter E-Mail
                            für ein Seminar, wird die Anmeldung automatisch als Referenten-Teilnahme
                            markiert — keine Seminargebühr, Bescheinigung wie regulär.
                        </CardDescription>
                    </div>
                    <Button onClick={openCreate} data-testid="add-referent">
                        <Plus className="h-4 w-4 mr-1.5" /> Neu
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="py-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Lade…
                    </div>
                ) : items.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">Noch keine Referenten angelegt.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground border-b">
                                <tr>
                                    <th className="text-left py-2 pr-4 font-medium">Name</th>
                                    <th className="text-left py-2 pr-4 font-medium">E-Mail</th>
                                    <th className="text-left py-2 pr-4 font-medium">Aktiv</th>
                                    <th className="text-right py-2 pl-4 font-medium">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(r => (
                                    <tr key={r.id} className="border-b last:border-0" data-testid={`referent-row-${r.id}`}>
                                        <td className="py-2 pr-4">
                                            <div className="font-medium">{r.display_name}</div>
                                        </td>
                                        <td className="py-2 pr-4 text-muted-foreground">{r.email || '—'}</td>
                                        <td className="py-2 pr-4">
                                            <Switch
                                                checked={r.active !== false}
                                                onCheckedChange={(v) => toggleActive(r, v)}
                                                data-testid={`referent-active-${r.id}`}
                                            />
                                        </td>
                                        <td className="py-2 pl-4 text-right">
                                            <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-referent-${r.id}`}>
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`delete-referent-${r.id}`}>
                                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>

            <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeDialog(); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing?.__new ? 'Referent anlegen' : 'Referent bearbeiten'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Titel (optional)</Label>
                            <Input value={form.title}
                                   onChange={e => setForm({ ...form, title: e.target.value })}
                                   placeholder="z. B. Dr. / Prof. / Dipl.-Päd." data-testid="referent-title" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Vorname *</Label>
                                <Input value={form.first_name}
                                       onChange={e => setForm({ ...form, first_name: e.target.value })}
                                       data-testid="referent-first-name" />
                            </div>
                            <div>
                                <Label>Nachname *</Label>
                                <Input value={form.last_name}
                                       onChange={e => setForm({ ...form, last_name: e.target.value })}
                                       data-testid="referent-last-name" />
                            </div>
                        </div>
                        <div>
                            <Label>E-Mail (optional)</Label>
                            {/*
                              Uncontrolled Input + Ref — umgeht Firefox-spezifische Probleme
                              mit React-controlled inputs, bei denen die Re-Render-Schleife
                              Eingaben verschlucken kann. Wert wird beim Speichern via Ref
                              ausgelesen. Mit `key={editing.id}` wird das Feld neu gemountet,
                              sobald ein anderer Referent bearbeitet wird, sodass
                              `defaultValue` frisch greift.
                            */}
                            <input
                                key={editing?.id || 'new'}
                                ref={emailRef}
                                type="text"
                                defaultValue={form.email}
                                autoComplete="off"
                                placeholder="fuer.auto-matching@…"
                                data-testid="referent-email"
                                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Wird eine E-Mail hinterlegt, erkennt das System bei Buchungen automatisch,
                                dass es sich um Referenten-Teilnahme handelt (keine Zahlungsforderung).
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}>Abbrechen</Button>
                        <Button onClick={save} disabled={saving} data-testid="save-referent">
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

export default ReferentsSettings;
